import type { Cart as CartType } from '@grafikr/shopify-typescript/type/json/Cart';
import type { LineItem as LineItemType } from '@grafikr/shopify-typescript/type/json/LineItem';
import Queue from 'utils/queue';

type CartError = {
  description: string;
  message: string;
  status: number;
};

type ResponseHandler<T> = {
  success?: (body: T) => void;
  error?: (body: CartError) => void;
};

export default new (class Cart {
  private queue: Queue;

  constructor() {
    this.queue = new Queue();
  }

  private sendRequest(input: RequestInfo | URL, init?: RequestInit) {
    return this.queue.add(() => fetch(input, init));
  }

  private static handleResponse<T>(response: Response, { success, error }: ResponseHandler<T>) {
    response.json().then((body) => {
      if (response.ok && success) {
        success(body);
      }

      if (!response.ok && error) {
        error(body);
      }
    });
  }

  async add(
    data: FormData,
    handler: ResponseHandler<{ items: LineItemType[] } | LineItemType>,
  ): Promise<void> {
    const response = await this.sendRequest(`${theme.routes.cartAdd}.js`, {
      method: 'post',
      body: data,
    });

    Cart.handleResponse(response, handler);
  }

  async get(handler: ResponseHandler<CartType>): Promise<void> {
    const response = await this.sendRequest(`${theme.routes.cart}.js`);

    Cart.handleResponse(response, handler);
  }

  async update(data: FormData, handler: ResponseHandler<CartType>): Promise<void> {
    const response = await this.sendRequest(`${theme.routes.cartUpdate}.js`, {
      method: 'post',
      body: data,
    });

    Cart.handleResponse(response, handler);
  }

  async change(data: FormData, handler: ResponseHandler<CartType>): Promise<void> {
    const response = await this.sendRequest(`${theme.routes.cartChange}.js`, {
      method: 'post',
      body: data,
    });

    Cart.handleResponse(response, handler);
  }

  async clear(handler: ResponseHandler<CartType>): Promise<void> {
    const response = await this.sendRequest(theme.routes.cartClear, {
      method: 'post',
    });

    Cart.handleResponse(response, handler);
  }
})();
